import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import { strategies, type Strategy, type StrategyCreate } from "@/api/strategies";
import { tradingAccounts, type TradingAccount } from "@/api/tradingAccounts";
import { Badge, Button, Card, CardBody, FormError, Input, Label, Select } from "@/components/ui";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";

export function StrategiesPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const list = useQuery({ queryKey: ["strategies"], queryFn: () => strategies.list() });
  const classes = useQuery({ queryKey: ["strategy-classes"], queryFn: () => strategies.classes() });
  const [open, setOpen] = useState(false);

  function refresh() {
    qc.invalidateQueries({ queryKey: ["strategies"] });
  }

  // Calling useMutation N times keeps the hook order stable even though
  // each action has a different return type.
  function makeAction(action: "start" | "stop" | "pause" | "resume" | "delete") {
    return useMutation<unknown, Error, string>({
      mutationFn: (id) => (strategies[action] as (id: string) => Promise<unknown>)(id),
      onSuccess: () => {
        toast.push(`Strategy ${action}ed`, "success");
        refresh();
      },
      onError: (e) =>
        toast.push(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e), "error"),
    });
  }

  const start = makeAction("start");
  const stop = makeAction("stop");
  const pause = makeAction("pause");
  const resume = makeAction("resume");
  const remove = makeAction("delete");

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Strategies</h1>
        <Button onClick={() => setOpen(true)}>New strategy</Button>
      </div>

      <Card>
        <CardBody className="overflow-x-auto">
          {list.isLoading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : list.data?.length === 0 ? (
            <p className="text-sm text-slate-500">
              No strategies yet. Click <strong>New strategy</strong> to register one.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="py-2">Name</th>
                  <th>Class</th>
                  <th>Status</th>
                  <th>Last checkpoint</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {list.data?.map((s) => (
                  <tr key={s.id} className="border-t border-slate-100 dark:border-slate-800 align-middle">
                    <td className="py-2 font-medium">{s.name}</td>
                    <td className="text-xs text-slate-500">{s.robot_class ?? "—"}</td>
                    <td>{statusBadge(s.status)}</td>
                    <td className="text-xs text-slate-500">
                      {s.last_checkpoint_at ? new Date(s.last_checkpoint_at).toLocaleString() : "never"}
                    </td>
                    <td>
                      <div className="flex justify-end gap-1">
                        {s.status !== "running" ? (
                          <Button variant="primary" onClick={() => (s.status === "paused" ? resume.mutate(s.id) : start.mutate(s.id))}>
                            {s.status === "paused" ? "Resume" : "Start"}
                          </Button>
                        ) : (
                          <>
                            <Button variant="secondary" onClick={() => pause.mutate(s.id)}>
                              Pause
                            </Button>
                            <Button variant="secondary" onClick={() => stop.mutate(s.id)}>
                              Stop
                            </Button>
                          </>
                        )}
                        <Button
                          variant="danger"
                          disabled={s.status === "running"}
                          onClick={() => {
                            if (confirm(`Delete strategy ${s.name}?`)) remove.mutate(s.id);
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <CreateStrategyModal
        open={open}
        onClose={() => setOpen(false)}
        availableClasses={classes.data?.strategies ?? []}
      />
    </div>
  );
}

function statusBadge(s: Strategy["status"]) {
  switch (s) {
    case "running":
      return <Badge tone="good">running</Badge>;
    case "paused":
      return <Badge tone="warn">paused</Badge>;
    case "stopped":
      return <Badge>stopped</Badge>;
    case "inactive":
      return <Badge>inactive</Badge>;
  }
}

function CreateStrategyModal({
  open,
  onClose,
  availableClasses,
}: {
  open: boolean;
  onClose: () => void;
  availableClasses: string[];
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState("");
  const [robotClass, setRobotClass] = useState(availableClasses[0] ?? "");
  const [accountId, setAccountId] = useState<string>("");
  const [configText, setConfigText] = useState<string>("{}");
  const [error, setError] = useState<string | null>(null);

  // Refresh the default class once the list arrives.
  useEffect(() => {
    if (!robotClass && availableClasses.length > 0) {
      setRobotClass(availableClasses[0]);
    }
  }, [availableClasses, robotClass]);

  const accounts = useQuery({
    queryKey: ["trading-accounts", "mine"],
    queryFn: () => tradingAccounts.list(true),
    enabled: open,
  });

  // Pull the parameter schema for the selected class. The modal shows
  // the example/default config as a starting point and validates that
  // the user's edits parse as JSON.
  const schema = useQuery({
    queryKey: ["strategy-schema", robotClass],
    queryFn: () => strategies.schema(robotClass),
    enabled: open && !!robotClass,
  });

  const defaultConfig = useMemo(() => {
    const s = schema.data as Record<string, unknown> | undefined;
    const example = (s?.example_config ?? s?.default_config ?? null) as
      | Record<string, unknown>
      | null;
    return example ? JSON.stringify(example, null, 2) : "{}";
  }, [schema.data]);

  // Sync the textarea to the chosen class's example config — but
  // don't clobber the user's edits once they've started typing.
  useEffect(() => {
    if (configText === "" || configText === "{}") {
      setConfigText(defaultConfig);
    }
  }, [defaultConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = useMutation({
    mutationFn: (body: StrategyCreate) => strategies.create(body),
    onSuccess: () => {
      toast.push("Strategy created", "success");
      qc.invalidateQueries({ queryKey: ["strategies"] });
      onClose();
      setName("");
      setConfigText("{}");
      setAccountId("");
    },
    onError: (e) =>
      setError(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    let parsed: Record<string, unknown> = {};
    if (configText.trim()) {
      try {
        parsed = JSON.parse(configText);
        if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
          throw new Error("Config must be a JSON object.");
        }
      } catch (err) {
        setError(`Config is not valid JSON: ${(err as Error).message}`);
        return;
      }
    }
    create.mutate({
      name,
      robot_class: robotClass || null,
      config: parsed,
      account_id: accountId || null,
    });
  }

  const accountList: TradingAccount[] = accounts.data ?? [];

  return (
    <Modal open={open} onClose={onClose} title="New strategy">
      <form onSubmit={submit} className="space-y-3">
        <div>
          <Label>Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} required />
          <p className="mt-1 text-xs text-slate-500">
            A unique label per user. Orders generated by this strategy use this name
            in <code>strategy_id</code>.
          </p>
        </div>
        <div>
          <Label>Strategy class</Label>
          {availableClasses.length > 0 ? (
            <Select value={robotClass} onChange={(e) => setRobotClass(e.target.value)}>
              {availableClasses.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </Select>
          ) : (
            <Input
              value={robotClass}
              onChange={(e) => setRobotClass(e.target.value)}
              placeholder="example_momentum"
            />
          )}
        </div>
        <div>
          <Label>Trading account</Label>
          <Select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            <option value="">— None (legacy / unbound) —</option>
            {accountList.map((a) => (
              <option key={a.id} value={a.id}>
                {a.login_code} ({a.base_currency})
              </option>
            ))}
          </Select>
          <p className="mt-1 text-xs text-slate-500">
            Optional. Bind orders generated by this strategy to a specific
            trading account so margin and statements are scoped correctly.
          </p>
        </div>
        <div>
          <Label>Config (JSON)</Label>
          <textarea
            className="block h-40 w-full rounded border border-slate-300 dark:border-slate-700 bg-transparent p-2 font-mono text-xs"
            value={configText}
            onChange={(e) => setConfigText(e.target.value)}
            spellCheck={false}
          />
          <p className="mt-1 text-xs text-slate-500">
            {schema.isLoading
              ? "Loading parameter schema…"
              : schema.data
              ? "Pre-filled from the strategy class's example config — edit before submitting."
              : "Strategy class has no published schema; pass any JSON the class accepts."}
          </p>
        </div>
        <FormError>{error}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={create.isPending}>
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
}
